import { useMemo, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/molecules/Dialog';
import { Button } from '@/components/atoms/Button';
import { Checkbox } from '@/components/atoms/Checkbox';
import {
  importCompanyStudents,
  type ImportStudentsResult,
} from '@/services/api/backoffice.api';
import { decodeCsv, fullName, parseStudentsCsv, type ParsedStudent } from './parseStudentsCsv';
import styles from './ImportStudentsDialog.module.scss';

export interface ImportStudentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  /** Correos ya dados de alta en la empresa, para marcarlos en la previsualización. */
  existingEmails: string[];
  /** Se llama al terminar una importación con al menos un alta. */
  onImported?: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  created: 'Creado e invitado',
  resent: 'Invitación reenviada',
  skipped: 'Ya existía',
  email_failed: 'Creado, pero el correo falló',
  invalid: 'Fila no válida',
};

export function ImportStudentsDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  existingEmails,
  onImported,
}: ImportStudentsDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [students, setStudents] = useState<ParsedStudent[]>([]);
  const [invalidLines, setInvalidLines] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<ImportStudentsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const existing = useMemo(
    () => new Set(existingEmails.map((e) => e.toLowerCase())),
    [existingEmails]
  );

  const nuevosSeleccionados = students.filter(
    (s) => selected.has(s.email) && !existing.has(s.email)
  ).length;
  const reenviosSeleccionados = students.filter(
    (s) => selected.has(s.email) && existing.has(s.email)
  ).length;
  const totalSeleccionado = nuevosSeleccionados + reenviosSeleccionados;

  // Cabecera: marcada con todas, indeterminada si solo hay algunas.
  const headerChecked: boolean | 'indeterminate' =
    students.length > 0 && selected.size === students.length
      ? true
      : selected.size === 0
        ? false
        : 'indeterminate';

  const reset = () => {
    setFileName(null);
    setStudents([]);
    setInvalidLines([]);
    setSelected(new Set());
    setResult(null);
    setError(null);
    setIsImporting(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const toggleRow = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(students.map((s) => s.email)) : new Set());
  };

  const handleFile = async (file: File) => {
    setError(null);
    setResult(null);
    try {
      const parsed = parseStudentsCsv(decodeCsv(await file.arrayBuffer()));
      setFileName(file.name);
      setStudents(parsed.students);
      setInvalidLines(parsed.invalidLines);
      // Se marcan solo los que no tienen cuenta: reenviar a quien ya la tiene
      // es una decisión explícita, marcando su fila.
      setSelected(
        new Set(parsed.students.filter((s) => !existing.has(s.email)).map((s) => s.email))
      );
      if (parsed.students.length === 0) {
        setError('No se ha encontrado ninguna fila con un correo válido.');
      }
    } catch {
      setError('No se ha podido leer el fichero.');
    }
  };

  const handleImport = async () => {
    const aEnviar = students.filter((s) => selected.has(s.email));
    if (aEnviar.length === 0) return;
    setIsImporting(true);
    setError(null);
    try {
      const res = await importCompanyStudents(
        companyId,
        aEnviar.map((s) => ({ name: fullName(s), email: s.email })),
        // Haber marcado una fila que ya existe es el consentimiento al reenvío.
        reenviosSeleccionados > 0
      );
      setResult(res);
      if (res.created > 0 || res.emailFailed > 0) onImported?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se ha podido completar la importación.');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* lg: la tabla tiene cinco columnas y en 32rem se apelotona. */}
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Importar alumnos desde CSV</DialogTitle>
          <DialogDescription>
            Sube un CSV con las columnas <strong>Nombre, Apellido y Email</strong>; si solo trae
            nombre y correo también vale. Se crearán las cuentas marcadas que falten en{' '}
            {companyName} y se les enviará la invitación manual.
          </DialogDescription>
        </DialogHeader>

        <div className={styles.importDialog__body}>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className={styles.importDialog__file}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />

          {!result && (
            <Button
              variant="secondary"
              leftIcon={<Upload size={16} aria-hidden />}
              onClick={() => inputRef.current?.click()}
            >
              {fileName ? 'Elegir otro fichero' : 'Seleccionar CSV'}
            </Button>
          )}

          {fileName && !result && (
            <p className={styles.importDialog__fileName}>
              {fileName} · {students.length} fila{students.length === 1 ? '' : 's'} detectada
              {students.length === 1 ? '' : 's'}
            </p>
          )}

          {!result && students.length > 0 && (
            <>
              <p className={styles.importDialog__summary}>
                <strong>{nuevosSeleccionados}</strong> se crearán e invitarán
                {reenviosSeleccionados > 0 &&
                  ` · ${reenviosSeleccionados} recibirán de nuevo la invitación`}
              </p>

              <div className={styles.importDialog__tableWrap}>
                <table className={styles.importDialog__table}>
                  <thead>
                    <tr>
                      <th scope="col" className={styles.importDialog__checkCell}>
                        <Checkbox
                          checked={headerChecked}
                          onCheckedChange={toggleAll}
                          aria-label="Seleccionar todas las filas"
                        />
                      </th>
                      <th scope="col">Nombre</th>
                      <th scope="col">Apellido</th>
                      <th scope="col">Email</th>
                      <th scope="col">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => {
                      const yaExiste = existing.has(s.email);
                      return (
                        <tr key={s.email}>
                          <td className={styles.importDialog__checkCell}>
                            <Checkbox
                              checked={selected.has(s.email)}
                              onCheckedChange={() => toggleRow(s.email)}
                              aria-label={`Seleccionar ${fullName(s)}`}
                            />
                          </td>
                          <td>{s.name}</td>
                          <td>{s.surname || '—'}</td>
                          <td className={styles.importDialog__email}>{s.email}</td>
                          <td>
                            <span
                              className={
                                yaExiste
                                  ? styles['importDialog__tag--skip']
                                  : styles['importDialog__tag--new']
                              }
                            >
                              {yaExiste ? 'Ya existe' : 'Nuevo'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {invalidLines.length > 0 && !result && (
            <p className={styles.importDialog__warn}>
              {invalidLines.length} línea{invalidLines.length === 1 ? '' : 's'} se ignorarán por
              no tener nombre y correo válidos.
            </p>
          )}

          {error && (
            <p className={styles.importDialog__error} role="alert">
              {error}
            </p>
          )}

          {result && (
            <>
              <p className={styles.importDialog__summary}>
                <strong>{result.created}</strong> creados e invitados
                {result.resent > 0 && ` · ${result.resent} reenviados`}
                {result.skipped > 0 && ` · ${result.skipped} ya existían`}
                {result.emailFailed > 0 && ` · ${result.emailFailed} sin correo`}
                {result.invalid > 0 && ` · ${result.invalid} no válidos`}
              </p>
              <div className={styles.importDialog__tableWrap}>
                <table className={styles.importDialog__table}>
                  <thead>
                    <tr>
                      <th scope="col">Nombre</th>
                      <th scope="col">Email</th>
                      <th scope="col">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results.map((r) => (
                      <tr key={r.email}>
                        <td>{r.name || '—'}</td>
                        <td className={styles.importDialog__email}>{r.email}</td>
                        <td>
                          <span
                            className={
                              r.status === 'created' || r.status === 'resent'
                                ? styles['importDialog__tag--new']
                                : styles['importDialog__tag--skip']
                            }
                          >
                            {STATUS_LABEL[r.status] ?? r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            {result ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!result && (
            <Button
              variant="primary"
              onClick={() => void handleImport()}
              isLoading={isImporting}
              disabled={totalSeleccionado === 0}
            >
              {nuevosSeleccionados === 0 ? 'Reenviar' : 'Crear e invitar'} ({totalSeleccionado})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImportStudentsDialog;
